import React, { useState, useRef, useEffect, useCallback } from "react";
import { useGoogleMapsLoader } from "../../../hooks/useGoogleMapsLoader";
import { isGoogleMapsReady, PLACES_STATUS_OK } from "../../../utils/googleMapsReady";
import {
  fetchAddressSuggestions,
  type AddressSuggestion,
  type PlacesLocation,
} from "../../../utils/placeAutocomplete";
import "./PlacesAutocomplete.css";

export type PlacesAddressComponents = google.maps.GeocoderAddressComponent[];

interface PlacesAutocompleteProps {
  label: string;
  value: string;
  onChange: (
    address: string,
    placeId: string,
    location: PlacesLocation,
    components: PlacesAddressComponents,
  ) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  hint?: string;
}

const EMPTY_LOCATION: PlacesLocation = { lat: 0, lng: 0 };
const DEBOUNCE_MS = 280;

const INPUT_CLASS =
  "h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:border-gray-700 dark:focus:border-brand-800";

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  label,
  value,
  onChange,
  type = "location",
  placeholder,
  required = false,
  error = false,
  hint,
}) => {
  const { isLoaded, error: mapsError } = useGoogleMapsLoader();
  const mapsActive = isLoaded && isGoogleMapsReady() && !mapsError;
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurCommit = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (!mapsActive) return;

    if (!autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!placesService.current) {
      const dummyDiv = document.createElement("div");
      placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
    }
    if (!geocoder.current) {
      geocoder.current = new window.google.maps.Geocoder();
    }
  }, [mapsActive]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const commitManualAddress = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || trimmed === (value || "").trim()) return;
    onChange(trimmed, "", EMPTY_LOCATION, []);
  }, [inputValue, value, onChange]);

  const runSearch = useCallback(
    (val: string) => {
      if (
        !val ||
        !mapsActive ||
        !autocompleteService.current ||
        !placesService.current ||
        !geocoder.current
      ) {
        setSuggestions([]);
        setLoading(false);
        return;
      }

      const id = ++requestId.current;
      setLoading(true);

      fetchAddressSuggestions(
        val,
        autocompleteService.current,
        geocoder.current,
        placesService.current,
      )
        .then((results) => {
          if (requestId.current !== id) return;
          setSuggestions(results);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setSuggestions([]);
        })
        .finally(() => {
          if (requestId.current !== id) return;
          setLoading(false);
        });
    },
    [mapsActive],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!val || !mapsActive) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => runSearch(val), DEBOUNCE_MS);
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    skipBlurCommit.current = true;

    if (!placesService.current || !mapsActive) {
      skipBlurCommit.current = false;
      return;
    }

    placesService.current.getDetails(
      {
        placeId: suggestion.placeId,
        fields: ["address_components", "geometry", "place_id", "formatted_address"],
      },
      (place, status) => {
        if (String(status) !== PLACES_STATUS_OK || !place) {
          skipBlurCommit.current = false;
          return;
        }

        const location =
          place.geometry?.location != null
            ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() }
            : EMPTY_LOCATION;
        const displayLabel = place.formatted_address ?? suggestion.description;
        setInputValue(displayLabel);
        setSuggestions([]);
        onChange(
          displayLabel,
          place.place_id ?? suggestion.placeId,
          location,
          place.address_components ?? [],
        );
        skipBlurCommit.current = false;
      },
    );
  };

  const handleBlur = () => {
    if (skipBlurCommit.current) return;
    commitManualAddress();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const inputClassName = `${INPUT_CLASS}${error ? " border-error-500 focus:ring-error-500/10" : ""}`;

  return (
    <div className="places-autocomplete relative space-y-2 text-gray-900">
      {label && (
        <label htmlFor={`${type}-location`} className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <div className="relative w-full overflow-visible">
        <div className="relative z-40">
          <input
            ref={inputRef}
            id={`${type}-location`}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            className={inputClassName}
            autoComplete="street-address"
          />
          {loading && mapsActive && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-brand-500" />
            </div>
          )}
        </div>
        {suggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            className="places-autocomplete-dropdown absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 shadow-lg"
            style={{ listStyle: "none", padding: 0, margin: 0 }}
          >
            {suggestions.map((s) => (
              <li
                key={s.placeId}
                className="places-autocomplete-option cursor-pointer px-4 py-2"
                onMouseDown={() => handleSuggestionClick(s)}
              >
                <span className="block text-sm text-gray-900">
                  {s.mainText ?? s.description}
                </span>
                {s.secondaryText && (
                  <span className="mt-0.5 block text-xs text-gray-500">{s.secondaryText}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {(hint || mapsError) && (
          <p className={`mt-1.5 text-xs ${error || mapsError ? "text-error-500" : "text-gray-500"}`}>
            {mapsError ? mapsError.message : hint}
          </p>
        )}
      </div>
    </div>
  );
};

export default PlacesAutocomplete;
